import type { Feature, FeatureCollection, Geometry } from 'geojson'

import { parseStamp, queryUrl } from './spc'

/**
 * Everything upstream-shaped about the National Hurricane Center's products,
 * the tropical twin of `spc.ts`: `geoJsonLayer.ts` is handed features whose
 * properties are *ours*, and the validity rules live here where a test can
 * hold them.
 *
 * The source is NOAA's `NHC_tropical_weather_summary` map service, probed
 * 2026-10-07 with three storms active (Isaias AT4, Rachel EP3, Simon EP5).
 * It carries every active storm in one layer per product, where its sibling
 * `NHC_tropical_weather` splits them over fifteen fixed slots (AT1-5, EP1-5,
 * CP1-5) — fifteen requests per product to find three storms. NHC's own
 * `CurrentStorms.json` would be the natural index but sends no CORS header,
 * and the map service does (`Access-Control-Allow-Origin` echoes the origin).
 *
 * **The service serves products that are no longer valid**, like SPC's. On the
 * day it was probed the Watch-Warning layer still held Rachel's lines from
 * advisory 13A, issued Sep 30 when she was off the Mexican coast; she was on
 * advisory 43 at 122°W with nothing anywhere near land. NHC leaves the last
 * watch/warning file in place once every one of them is cancelled, so nothing
 * in the payload says it lapsed except that a newer advisory exists. The rules:
 *
 * 1. A storm draws only if its **cone** was written in the last
 *    `STALE_HOURS`. Advisories are six-hourly without exception while NHC is
 *    advising, so two missed ones means it has stopped — and a final
 *    advisory's cone would otherwise stay up as a forecast nobody is making.
 * 2. Its track, its points and its watches/warnings draw only if that cone is
 *    in the same pass and their advisory is **not older than the cone's**.
 *    `13A` against `43` is the case above; `5A` against `5` is an
 *    intermediate advisory that changed the warnings and kept the cone, and
 *    it stays.
 *
 * `now` is a parameter for the same reason it is in `spc.ts`: the tests
 * assert the rule rather than the clock.
 */

const TROPICAL = 'https://mapservices.weather.noaa.gov/tropical/rest/services/tropical'
export const NHC_SUMMARY = `${TROPICAL}/NHC_tropical_weather_summary/MapServer`

export const NHC_ATTRIB = '<a href="https://www.nhc.noaa.gov/">NOAA/NWS National Hurricane Center</a>'

/** Two missed six-hourly issuances, for an advisory and for the outlook. */
export const STALE_HOURS = 12

/**
 * Layer numbers inside `NHC_SUMMARY`, by name. The order of `STORM_LAYERS` is
 * the order `parseStorms` receives the payloads in.
 */
export const LAYER = {
  points: 5,
  track: 6,
  cone: 7,
  watchWarning: 8,
  outlookAreas: 3,
} as const

/**
 * About 1 km of precision and 2 km of simplification. The cone layer is
 * 195 KB for three storms as served and 4 KB like this (measured
 * 2026-10-07), and a 2 km wobble on a cone hundreds of kilometres wide is
 * nothing anybody can see at a zoom where the whole cone is on screen.
 */
const TRIM = { geometryPrecision: '2', maxAllowableOffset: '0.02' }

export const STORM_LAYERS = [LAYER.cone, LAYER.track, LAYER.points, LAYER.watchWarning] as const

export function stormUrls(): string[] {
  return STORM_LAYERS.map((l) => queryUrl(NHC_SUMMARY, l, '1=1', TRIM))
}

export function outlookUrl(): string {
  return queryUrl(NHC_SUMMARY, LAYER.outlookAreas, '1=1', TRIM)
}

/**
 * What `geoJsonLayer.ts` paints and `probe.ts` reads. `rank`, `short` and
 * `full` are the names an SPC feature uses, so a tap on a cone goes through
 * the same `readFeature` an outlook does. No `from`/`to`: an advisory's
 * window would come from `advdate`, a local-time display string, and the menu
 * prints that verbatim instead of a parse of it.
 */
export type TropicalProps = {
  rank: number
  /** Drawn on the map: H/S/D/M on a point, nothing on a cone or a line. */
  short: string
  /** What the menu says. Empty on lines, which are not a tap target. */
  full: string
  fill: string
  stroke: string
  /** Line width in px; absent means the layer's default. */
  width?: number
  /** Set on a storm's current position only: its name, beside the circle. */
  name?: string
}

type TropicalFeature = Feature<Geometry, TropicalProps>

/**
 * Colours. These are ours: NHC's payload carries none, unlike SPC's, so the
 * table is keyed on NHC's own category codes and an unknown code is dropped
 * rather than painted grey — a line nobody can read the meaning of is a
 * claim with no content.
 *
 * Watches and warnings follow NHC's own map convention (hurricane warning
 * red, hurricane watch pink, tropical storm warning blue, tropical storm
 * watch yellow), because that is the key anybody who has seen a cone graphic
 * already holds.
 */
const WATCH_WARNING: Record<string, { color: string; label: string }> = {
  HWR: { color: '#e8202a', label: 'Hurricane Warning' },
  HWA: { color: '#f58ac0', label: 'Hurricane Watch' },
  TWR: { color: '#2b5fd9', label: 'Tropical Storm Warning' },
  TWA: { color: '#f2c91d', label: 'Tropical Storm Watch' },
}

/** A forecast point's development label: NHC draws the same letters. */
const DEVELOPMENT: Record<string, { fill: string; rank: number }> = {
  // Post-tropical, remnant low or dissipated: still a forecast position.
  L: { fill: '#a0a7b0', rank: 1 },
  D: { fill: '#5b9bd5', rank: 2 },
  S: { fill: '#f2b33d', rank: 3 },
  H: { fill: '#e5462c', rank: 4 },
  // Lighter than NHC's purple, so the dark letter on it reads.
  M: { fill: '#d36ac4', rank: 5 },
}

const STORM_TYPES: Record<string, string> = {
  HU: 'Hurricane',
  MH: 'Major Hurricane',
  TS: 'Tropical Storm',
  TD: 'Tropical Depression',
  STS: 'Subtropical Storm',
  SS: 'Subtropical Storm',
  SD: 'Subtropical Depression',
  PTC: 'Potential Tropical Cyclone',
  PC: 'Potential Tropical Cyclone',
  PT: 'Post-Tropical Cyclone',
}

/** Slate rather than NHC's white: a white cone over a light basemap is not
 *  there, and this one reads on both themes. */
export const CONE_FILL = '#9fb3c8'
export const CONE_STROKE = '#6f88a3'
export const TRACK_STROKE = '#3b4f66'
/** Ink for a point's outline and its letter. */
const POINT_INK = '#1f2733'

/** Outlook risk, NHC's own yellow/orange/red. */
const RISK: Record<string, { fill: string; stroke: string }> = {
  low: { fill: '#f7d417', stroke: '#c9a800' },
  medium: { fill: '#f59a23', stroke: '#c46f00' },
  high: { fill: '#e2332b', stroke: '#a91b14' },
}

function features(json: unknown): unknown[] {
  const fs = (json as { features?: unknown })?.features
  return Array.isArray(fs) ? fs : []
}

function props(f: unknown): Record<string, unknown> {
  const p = (f as { properties?: unknown })?.properties
  return p && typeof p === 'object' ? (p as Record<string, unknown>) : {}
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

/** Geometry with at least one coordinate of the kinds named, or null. */
function geometry(f: unknown, kinds: readonly string[]): Geometry | null {
  const g = (f as { geometry?: unknown })?.geometry as
    | { type?: string; coordinates?: unknown }
    | null
    | undefined
  if (!g || !g.type || !kinds.includes(g.type)) return null
  const c = g.coordinates
  if (!Array.isArray(c) || c.length === 0) return null
  return g as Geometry
}

const AREAS = ['Polygon', 'MultiPolygon'] as const
const LINES = ['LineString', 'MultiLineString'] as const
const POINTS = ['Point'] as const

/** `"13A"` -> 13. An intermediate advisory carries the number it follows. */
export function advisoryNumber(raw: unknown): number | null {
  const m = /^\s*(\d+)/.exec(String(raw ?? ''))
  return m ? Number(m[1]) : null
}

/** One storm's identity across the four layers: `binnumber` is a *slot* and
 *  is reused for the next storm once this one ends, so the storm number rides
 *  along with it. */
function stormKey(p: Record<string, unknown>): string | null {
  const bin = text(p.binnumber)
  const num = Number(p.stormnum)
  return bin && Number.isFinite(num) ? `${bin}/${num}` : null
}

interface Storm {
  advisory: number
  name: string
}

/**
 * All active storms, from the four payloads in `STORM_LAYERS` order, checked
 * against `now` and against each other as the header describes.
 *
 * Drawn bottom to top by the order they are pushed, which is also the order
 * MapLibre falls back to within a layer: cones, then tracks, then warnings,
 * then points.
 */
export function parseStorms(json: unknown, now: number): FeatureCollection<Geometry, TropicalProps> {
  const [cones, tracks, points, warnings] = Array.isArray(json) ? json : []
  const storms = new Map<string, Storm>()
  const out: TropicalFeature[] = []

  for (const f of features(cones)) {
    const g = geometry(f, AREAS)
    const p = props(f)
    const key = stormKey(p)
    const advisory = advisoryNumber(p.advisnum)
    const written = Number(p.idp_filedate)
    const name = text(p.stormname)
    if (!g || !key || advisory === null || !name) continue
    if (!Number.isFinite(written) || now - written > STALE_HOURS * 3600_000) continue
    storms.set(key, { advisory, name })
    const kind = STORM_TYPES[text(p.stormtype).toUpperCase()] ?? 'Tropical Cyclone'
    const issued = advisoryTime(p.advdate)
    out.push({
      type: 'Feature',
      geometry: g,
      properties: {
        rank: 1,
        // The name goes beside the storm's current position instead: a
        // polygon's label lands wherever MapLibre finds room, which on a
        // five-day cone is two states from the storm.
        short: '',
        full: `${kind} ${name} · advisory ${text(p.advisnum)}${issued ? `, ${issued}` : ''}`,
        fill: CONE_FILL,
        stroke: CONE_STROKE,
        width: 1,
      },
    })
  }

  /** The storm this feature belongs to, if it is current and not older than its cone. */
  const current = (p: Record<string, unknown>): Storm | null => {
    const key = stormKey(p)
    const storm = key ? storms.get(key) : undefined
    const advisory = advisoryNumber(p.advisnum)
    return storm && advisory !== null && advisory >= storm.advisory ? storm : null
  }

  for (const f of features(tracks)) {
    const g = geometry(f, LINES)
    if (!g || !current(props(f))) continue
    out.push({
      type: 'Feature',
      geometry: g,
      properties: { rank: 0, short: '', full: '', fill: TRACK_STROKE, stroke: TRACK_STROKE, width: 1.5 },
    })
  }

  for (const f of features(warnings)) {
    const g = geometry(f, LINES)
    const p = props(f)
    const ww = WATCH_WARNING[text(p.tcww).toUpperCase()]
    if (!g || !ww || !current(p)) continue
    out.push({
      type: 'Feature',
      geometry: g,
      properties: { rank: 0, short: '', full: '', fill: ww.color, stroke: ww.color, width: 4 },
    })
  }

  for (const f of features(points)) {
    const g = geometry(f, POINTS)
    const p = props(f)
    const dev = DEVELOPMENT[text(p.dvlbl).toUpperCase()]
    const storm = current(p)
    if (!g || !dev || !storm) continue
    out.push({
      type: 'Feature',
      geometry: g,
      properties: {
        rank: dev.rank,
        short: text(p.dvlbl).toUpperCase(),
        full: '',
        fill: dev.fill,
        stroke: POINT_INK,
        // `tau` is the forecast hour; 0 is where the storm is now.
        ...(Number(p.tau) === 0 ? { name: storm.name } : {}),
      },
    })
  }

  return { type: 'FeatureCollection', features: out }
}

/**
 * `"400 PM CDT Wed Oct 07 2026"` -> `"4:00 PM CDT Wed"`. NHC's own display
 * string with the colon put back and the date dropped: a storm on screen is
 * at most `STALE_HOURS` old, so the weekday names it. Anything else is shown
 * as it came rather than guessed at.
 */
export function advisoryTime(raw: unknown): string {
  const s = text(raw)
  const m = /^(\d{1,2})(\d{2}) (AM|PM) ([A-Z]{2,4}) ([A-Z][a-z]{2}) [A-Z][a-z]{2} \d{1,2} \d{4}$/.exec(s)
  return m ? `${Number(m[1])}:${m[2]} ${m[3]} ${m[4]} ${m[5]}` : s
}

/** `"30%"` -> 30. */
function percent(raw: unknown): number | null {
  const m = /^\s*(\d{1,3})\s*%?\s*$/.exec(String(raw ?? ''))
  return m ? Number(m[1]) : null
}

/**
 * The seven-day Graphical Tropical Weather Outlook's areas.
 *
 * The product carries no expiry. Its issuance is in `idp_source` —
 * `gtwo_areas_202610071722` — and the outlook is issued every six hours in
 * season, so an area more than `STALE_HOURS` old is from an outlook that has
 * since been replaced or withdrawn and is dropped. `idp_filedate` is the
 * fallback, because a parse of a filename is the more fragile of the two.
 */
export function parseOutlook(json: unknown, now: number): FeatureCollection<Geometry, TropicalProps> {
  const out: TropicalFeature[] = []
  for (const f of features(json)) {
    const g = geometry(f, AREAS)
    const p = props(f)
    if (!g) continue
    const stamp = /(\d{12})$/.exec(text(p.idp_source))
    const issued = (stamp ? parseStamp(stamp[1]) : null) ?? Number(p.idp_filedate)
    if (!Number.isFinite(issued) || now - issued > STALE_HOURS * 3600_000) continue

    const risk = RISK[text(p.risk7day).toLowerCase()]
    const week = percent(p.prob7day)
    if (!risk || week === null) continue
    const two = percent(p.prob2day)
    out.push({
      type: 'Feature',
      geometry: g,
      properties: {
        rank: week,
        short: `${week}%`,
        full: `${week}% in 7 days${two === null ? '' : ` · ${two}% in 2 days`}`,
        fill: risk.fill,
        stroke: risk.stroke,
      },
    })
  }
  return { type: 'FeatureCollection', features: out }
}
